import { useCallback, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import NoFilesFound from '../../../../../../common/components/atoms/NoFilesFound';
import { formatDateTranslate } from '../../../../../../common/utils/date';
import { displaySnackbar } from '../../../../../../core/notifications/snackbarSlice';
import { useAppDispatch } from '../../../../../../store';
import { PublicFile } from '../../../entities/PublicFile';
import { DetailsDrawerProps } from '../../../molecules/DetailsDrawer';
import { FileExplorerProps } from '../../../molecules/FileExplorer';
import { BulkFileBlobDownloader } from '../../../utils/bulkDownload';
import { downloadPublicFile, downloadPublicFileBlob } from '../../../utils/downloadFile';
import { getFileName } from '../../../utils/file';
import { useFileFilterSystem } from '../../fileFilterSystem/hook';
import { useDetailsFile } from '../../hooks/detailsFile';
import { defaultComparer } from './comparer';
import { useFetchPublicFiles } from './fetchPublicFiles';
import { generateInitialState } from './filters';
import { getHeaders } from './headers';
import { generateRow } from './rows';

interface PublicFileExplorerProps extends FileExplorerProps<PublicFile, PublicFile> {}

export function usePublicFileExplorer(): PublicFileExplorerProps {
  const { t } = useTranslation('Repository');
  const dispatch = useAppDispatch();

  const { files, isLoading: isFilesLoading } = useFetchPublicFiles();

  const initialFileFilterSystemState = useMemo(() => generateInitialState(), []);

  const { filters, filteredFiles, getFile, setFiles, resetFilters } = useFileFilterSystem<PublicFile>(
    initialFileFilterSystemState,
    isFilesLoading,
  );

  useEffect(() => {
    if (files !== undefined) setFiles(files);
  }, [files, setFiles]);

  const headers = useMemo(() => getHeaders(t), [t]);

  const onDownloadFile = useCallback(
    (file: PublicFile) => () =>
      new Promise<void>((resolve) =>
        downloadPublicFile(file)
          .finally(() => resolve())
          .catch((err) => {
            console.error(err);
            dispatch(
              displaySnackbar({
                message: t('Errors.DownloadFileError'),
                severity: 'error',
              }),
            );
          }),
      ),
    [t, dispatch],
  );

  const getDetailsDataFile = useCallback<(file: PublicFile) => DetailsDrawerProps['data']>(
    (file: PublicFile) => [
      { title: t('FileExplorer.Details.FileName'), value: getFileName(file), marginBottom: 5 },
      {
        title: t('FileExplorer.Details.FileDescription'),
        value: file.description ?? t('FileExplorer.Details.FileDescriptionEmpty'),
        dense: true,
        marginBottom: 5,
      },
      { title: t('FileExplorer.Details.FileExtension'), value: file.extension, marginBottom: 1 },
      { title: t('FileExplorer.Details.FileSize'), value: file.size, marginBottom: 1 },
      {
        title: t('FileExplorer.Details.FilePublishingDate'),
        value: formatDateTranslate(t, 'FileExplorer.Details.FormattedDate', file.publishedDate),
        marginBottom: 5,
      },
    ],
    [t],
  );

  const { onDetails, detailsDrawerProps } = useDetailsFile(onDownloadFile, getDetailsDataFile);

  const generateRowCallback = useCallback<PublicFileExplorerProps['generateRow']>(
    (file: PublicFile) => {
      const onDownload = onDownloadFile(file);
      const onInfos = () => {
        onDetails(file);
      };

      return generateRow(file, t, onDownload, onInfos);
    },
    [t, onDownloadFile, onDetails],
  );

  const bulkFileBlobDownloader = useCallback<BulkFileBlobDownloader>(
    (fileIds) => {
      const filesToDownload = fileIds.map((id) => getFile(id)).filter((file) => file !== undefined) as PublicFile[];
      return filesToDownload.map(downloadPublicFileBlob);
    },
    [getFile],
  );

  const emptyFileTableComponent = <NoFilesFound title={t('FileExplorer.Table.NoFilesTitle')} />;

  return {
    files: filteredFiles,
    totalFilesCount: files?.length ?? 0,
    isFilesLoading,
    headers,
    generateRow: generateRowCallback,
    defaultComparer,
    bulkFileBlobDownloader,
    filters,
    resetFilters,
    emptyFileTableComponent,
    detailsDrawerProps,
  };
}
